import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import PhoneNumberInput from '../../components/PhoneNumberInput';

const GENDER_OPTIONS = ['남', '여'] as const;
const POSITION_OPTIONS = ['성도', '집사', '권사', '장로', '전도사', '목사'] as const;
const STATUS_OPTIONS = ['재적', '이명', '제적'] as const;
const BAPTISM_OPTIONS = ['미세례', '유아세례', '세례', '입교'] as const;

type MemberRow = {
  id: number;
  name: string;
  gender: string;
  birthDate: string | null;
  phone: string;
  address: string;
  position: string;
  department: string;
  joinedDate: string | null;
  status: string;
  baptism: string;
  note: string;
  photoUrl: string;
};

type MemberForm = {
  name: string;
  gender: string;
  birthDate: string;
  phone: string;
  address: string;
  position: string;
  department: string;
  joinedDate: string;
  status: string;
  baptism: string;
  note: string;
  // 웹엔 사진 업로드 UI가 없지만, 서버 PUT이 빠진 값을 ''로 덮어쓰므로 관리자앱에서 올린 사진을 그대로 돌려보낸다.
  photoUrl: string;
};

const EMPTY_FORM: MemberForm = {
  name: '',
  gender: '',
  birthDate: '',
  phone: '',
  address: '',
  position: '성도',
  department: '',
  joinedDate: '',
  status: '재적',
  baptism: '미세례',
  note: '',
  photoUrl: '',
};

function toDateInputValue(value: string | null) {
  if (!value) return '';
  return value.slice(0, 10);
}

const POSITION_BADGE: Record<string, string> = {
  목사: 'manage-badge--navy',
  전도사: 'manage-badge--navy',
  장로: 'manage-badge--blue',
  권사: 'manage-badge--green',
  집사: 'manage-badge--green',
  성도: 'manage-badge--gray',
};

const STATUS_BADGE: Record<string, string> = {
  재적: 'manage-badge--green',
  이명: 'manage-badge--amber',
  제적: 'manage-badge--red',
};

const BAPTISM_BADGE: Record<string, string> = {
  세례: 'manage-badge--blue',
  유아세례: 'manage-badge--purple',
  입교: 'manage-badge--blue',
  미세례: 'manage-badge--gray',
};

const ZONE_DOT_COLORS = ['#1d4ed8', '#6d28d9', '#047857', '#b45309', '#be185d', '#0f766e'];
const AVATAR_COLORS = ['#1d4ed8', '#6d28d9', '#047857', '#b45309', '#be185d', '#0f766e'];

function monthLabel(monthIndex: number) {
  return `${monthIndex + 1}월`;
}

/** 최근 6개월(이번 달 포함) 등록일 기준 신규 등록 건수 */
function useMonthlyRegistrations(rows: MemberRow[]) {
  return useMemo(() => {
    const now = new Date();
    const months: { key: string; label: string; count: number }[] = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: monthLabel(d.getMonth()), count: 0 });
    }
    const byKey = new Map(months.map((m) => [m.key, m]));
    rows.forEach((row) => {
      if (!row.joinedDate) return;
      const d = new Date(row.joinedDate);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const bucket = byKey.get(key);
      if (bucket) bucket.count += 1;
    });
    return months;
  }, [rows]);
}

/** 구역/부서(department)별 인원 집계 (내림차순) */
function useZoneBreakdown(rows: MemberRow[]) {
  return useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => {
      const key = row.department.trim() || '미배정';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);
}

/** 오늘부터 7일 이내 생일자 (연도 무관, 월-일 기준) */
function useUpcomingBirthdays(rows: MemberRow[]) {
  return useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return rows
      .map((row) => {
        if (!row.birthDate) return null;
        const bd = new Date(row.birthDate);
        if (Number.isNaN(bd.getTime())) return null;
        const next = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
        if (next < today) next.setFullYear(next.getFullYear() + 1);
        const diffDays = Math.round((next.getTime() - today.getTime()) / 86400000);
        if (diffDays > 7) return null;
        return { row, next, diffDays };
      })
      .filter((v): v is { row: MemberRow; next: Date; diffDays: number } => v !== null)
      .sort((a, b) => a.diffDays - b.diffDays)
      .slice(0, 6);
  }, [rows]);
}

function formatBirthdayLabel(date: Date) {
  const weekday = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
  return `${date.getMonth() + 1}/${date.getDate()} (${weekday})`;
}

export default function ManageMembers({ authChurch }: { authChurch: string }) {
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<MemberForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchMembers = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${MainURL}/church-member`, { params: { authChurch } })
      .then((res) => setRows(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /church-member', err);
        setError('교적 정보를 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch]);

  const openCreateForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (row: MemberRow) => {
    setEditingId(row.id);
    setForm({
      name: row.name,
      gender: row.gender,
      birthDate: toDateInputValue(row.birthDate),
      phone: row.phone,
      address: row.address,
      position: row.position,
      department: row.department,
      joinedDate: toDateInputValue(row.joinedDate),
      status: row.status,
      baptism: row.baptism,
      note: row.note,
      photoUrl: row.photoUrl ?? '',
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submitForm = () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const request = editingId
      ? axios.put(`${MainURL}/church-member/${editingId}`, { authChurch, ...form })
      : axios.post(`${MainURL}/church-member`, { authChurch, ...form });

    request
      .then(() => {
        closeForm();
        fetchMembers();
      })
      .catch((err) => console.error(editingId ? 'PUT /church-member/:id' : 'POST /church-member', err))
      .finally(() => setSaving(false));
  };

  const removeMember = (id: number) => {
    if (!window.confirm('이 성도 정보를 삭제할까요?')) return;
    axios
      .delete(`${MainURL}/church-member/${id}`, { params: { authChurch } })
      .then(() => fetchMembers())
      .catch((err) => console.error('DELETE /church-member/:id', err));
  };

  const filteredRows = useMemo(() => {
    const keyword = search.trim();
    if (!keyword) return rows;
    return rows.filter((row) => row.name.includes(keyword) || row.phone.includes(keyword));
  }, [rows, search]);

  const zoneBreakdown = useZoneBreakdown(rows);
  const monthlyRegistrations = useMonthlyRegistrations(rows);
  const maxMonthlyCount = Math.max(1, ...monthlyRegistrations.map((m) => m.count));
  const upcomingBirthdays = useUpcomingBirthdays(rows);
  const currentMonthCount = monthlyRegistrations[monthlyRegistrations.length - 1]?.count ?? 0;
  const baptizedCount = rows.filter((row) => row.baptism === '세례' || row.baptism === '입교').length;
  const activeCount = rows.filter((row) => row.status === '재적').length;

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">교적관리</h2>
          <p className="manage-panel-desc">교인 명부와 세례·직분·구역 정보를 관리합니다.</p>
        </div>
        <button type="button" className="manage-btn manage-btn--primary" onClick={openCreateForm}>
          + 성도 등록
        </button>
      </div>

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="manage-stats">
            <div className="manage-stat-card">
              <span>전체 등록 교인</span>
              <strong>{rows.length}<small> 명</small></strong>
            </div>
            <div className="manage-stat-card">
              <span>이번 달 신규 등록</span>
              <strong>{currentMonthCount}<small> 명</small></strong>
            </div>
            <div className="manage-stat-card">
              <span>세례·입교 교인</span>
              <strong>{baptizedCount}<small> 명</small></strong>
            </div>
            <div className="manage-stat-card">
              <span>재적 교인</span>
              <strong>{activeCount}<small> 명</small></strong>
            </div>
          </div>

          <div className="manage-filterbar">
            <div className="manage-filterbar-search">
              <input
                placeholder="이름 또는 연락처로 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="manage-grid-2col">
            <section className="manage-card-block">
              <div className="manage-block-head">
                <h3 className="manage-block-title">교인 명부</h3>
                <span className="manage-block-count">{filteredRows.length}</span>
              </div>
              <div className="manage-table-wrap">
                <table className="manage-table">
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th style={{ width: 90 }}>직분</th>
                      <th style={{ width: 110 }}>구역/부서</th>
                      <th style={{ width: 82 }}>세례</th>
                      <th style={{ width: 130 }}>연락처</th>
                      <th style={{ width: 70 }}>상태</th>
                      <th style={{ width: 140 }}>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="manage-table-empty">등록된 성도가 없습니다.</td>
                      </tr>
                    )}
                    {filteredRows.map((row, index) => (
                      <tr key={row.id}>
                        <td>
                          <div className="manage-mcell">
                            <span className={`manage-avatar manage-avatar--${(index % 6) + 1}`}>{row.name.slice(0, 1)}</span>
                            <div>
                              <b>{row.name}</b>
                              <small>{toDateInputValue(row.birthDate) || '생년월일 미등록'} · {row.gender || '-'}</small>
                            </div>
                          </div>
                        </td>
                        <td><span className={`manage-badge ${POSITION_BADGE[row.position] ?? 'manage-badge--gray'}`}>{row.position}</span></td>
                        <td className="manage-table-meta">{row.department}</td>
                        <td><span className={`manage-badge ${BAPTISM_BADGE[row.baptism] ?? 'manage-badge--gray'}`}>{row.baptism}</span></td>
                        <td className="manage-table-meta" style={{ whiteSpace: 'nowrap' }}>{row.phone}</td>
                        <td><span className={`manage-badge ${STATUS_BADGE[row.status] ?? 'manage-badge--gray'}`}>{row.status}</span></td>
                        <td>
                          <div className="manage-row-actions">
                            <button type="button" className="manage-btn manage-btn--sm" onClick={() => openEditForm(row)}>수정</button>
                            <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={() => removeMember(row.id)}>삭제</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="manage-side-stack">
              <div className="manage-side-card">
                <div className="manage-side-card-head">🗺 구역별 현황</div>
                <div className="manage-side-card-body">
                  {zoneBreakdown.length === 0 && <p className="manage-side-empty">등록된 성도가 없습니다.</p>}
                  {zoneBreakdown.map((zone, index) => (
                    <div className="manage-stat-row" key={zone.name}>
                      <span
                        className="manage-stat-row-dot"
                        style={{ background: ZONE_DOT_COLORS[index % ZONE_DOT_COLORS.length] }}
                      />
                      <span className="manage-stat-row-label">{zone.name}</span>
                      <span className="manage-stat-row-count">{zone.count}명</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="manage-side-card">
                <div className="manage-side-card-head">📈 월별 신규 등록 교인</div>
                <div className="manage-side-card-body">
                  <div className="manage-mini-chart">
                    {monthlyRegistrations.map((m, index) => (
                      <div className="manage-mini-chart-col" key={m.key}>
                        <div
                          className={`manage-mini-chart-bar${index === monthlyRegistrations.length - 1 ? ' is-current' : ''}`}
                          style={{ height: `${Math.max(4, (m.count / maxMonthlyCount) * 100)}%` }}
                          title={`${m.label} ${m.count}명`}
                        />
                        <span className="manage-mini-chart-label">{m.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="manage-side-card">
                <div className="manage-side-card-head">🎂 이번 주 생일 교인</div>
                <div className="manage-side-card-body">
                  {upcomingBirthdays.length === 0 && <p className="manage-side-empty">이번 주 생일자가 없습니다.</p>}
                  {upcomingBirthdays.map(({ row, next }, index) => (
                    <div className="manage-birthday-row" key={row.id}>
                      <span
                        className="manage-birthday-avatar"
                        style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
                      >
                        {row.name.slice(0, 1)}
                      </span>
                      <span>{row.name} {row.position}</span>
                      <span className="manage-birthday-date">{formatBirthdayLabel(next)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {formOpen && (
        <div className="manage-modal-overlay" onClick={closeForm}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editingId ? '성도 정보 수정' : '성도 등록'}</h3>
              <button type="button" className="manage-modal-close" onClick={closeForm}>×</button>
            </div>
            <div className="manage-modal-body">
              <div className="manage-form manage-form--grid">
                <label className="manage-field">
                  <span>이름</span>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>성별</span>
                  <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                    <option value="">선택 안 함</option>
                    {GENDER_OPTIONS.map((gender) => (
                      <option key={gender} value={gender}>{gender}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>생년월일</span>
                  <input type="date" value={form.birthDate} onChange={(e) => setForm({ ...form, birthDate: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>연락처</span>
                  <PhoneNumberInput value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
                </label>
                <label className="manage-field manage-field--full">
                  <span>주소</span>
                  <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>직분</span>
                  <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>
                    {POSITION_OPTIONS.map((position) => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>구역/부서</span>
                  <input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="예: 1구역, 청년부" />
                </label>
                <label className="manage-field">
                  <span>등록일</span>
                  <input type="date" value={form.joinedDate} onChange={(e) => setForm({ ...form, joinedDate: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>재적상태</span>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>세례</span>
                  <select value={form.baptism} onChange={(e) => setForm({ ...form, baptism: e.target.value })}>
                    {BAPTISM_OPTIONS.map((baptism) => (
                      <option key={baptism} value={baptism}>{baptism}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field manage-field--full">
                  <span>비고</span>
                  <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </label>
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={closeForm}>취소</button>
              <button
                type="button"
                className="manage-btn manage-btn--primary"
                onClick={submitForm}
                disabled={saving || !form.name.trim()}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
